import { createContext, useContext, useState, useRef, useEffect } from 'react';
import { Button, Modal } from '@ds';
import modalStyles from './PassportClickInterceptor.module.scss';
import warningIcon from 'src/assets/icons/warning.svg';
import { ButtonStyle, ButtonVariant } from '@ds/elements/Button/Button.types';
import { useNavigate, useLocation } from 'react-router-dom';

interface InterceptorContext {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  showLeaveModal: boolean;
  setShowLeaveModal: (show: boolean) => void;
  handleLeave: () => void;
  setPendingLeaveTarget: (target: EventTarget | null) => void;
  pendingUrl: string | null;
  setPendingUrl: (url: string | null) => void;
}

const Context = createContext<InterceptorContext | null>(null);

export const InterceptorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [enabled, setEnabled] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingLeaveTarget, setPendingLeaveTarget] = useState<EventTarget | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [isBackButtonTriggered, setIsBackButtonTriggered] = useState(false);
  const navigationHistory = useRef<string[]>([]);
  const extraHistoryEntries = useRef(0);
  const isSyntheticClick = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Track navigation history
  useEffect(() => {
    const currentPath = location.pathname;
    const history = navigationHistory.current;

    // Only add if it's different from the last entry
    if (history.length === 0 || history[history.length - 1] !== currentPath) {
      navigationHistory.current = [...history, currentPath];
    }
  }, [location.pathname]);

  const handleLeave = () => {
    setShowLeaveModal(false);
    setEnabled(false);
    setPendingUrl(null);

    // Reset extra history entries when leaving normally
    extraHistoryEntries.current = 0;

    if (pendingLeaveTarget && pendingLeaveTarget instanceof HTMLElement) {
      isSyntheticClick.current = true;
      setTimeout(() => {
        pendingLeaveTarget.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      }, 0);
    }
    setPendingLeaveTarget(null);
  };

  // Enhanced onClose function to handle modal close and navigation reset
  const handleModalClose = () => {
    setShowLeaveModal(false);
    setPendingUrl(null);
    setPendingLeaveTarget(null);
    setIsBackButtonTriggered(false);
  };

  // Handler specifically for back button navigation
  const handleBackButtonLeave = () => {
    setShowLeaveModal(false);
    setEnabled(false);
    setIsBackButtonTriggered(false);

    // Check if we have enough navigation history
    // If history length <= 1, it means user likely came via direct URL entry
    const hasNavigationHistory = navigationHistory.current.length > 1;

    // Calculate the correct navigation offset based on extra history entries
    // Each time we enable the interceptor, we add 1 extra history entry via pushState
    const totalOffset = hasNavigationHistory ? extraHistoryEntries.current : extraHistoryEntries.current + 1;

    // Reset the extra history entries after navigation
    extraHistoryEntries.current = 0;

    navigate(-totalOffset);
  };

  // Handle browser close, tab close, or page reload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!enabled) return;
      e.preventDefault();
    };

    if (enabled) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled]);

  // Handle native back button
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (!enabled) return;

      // Prevent the default back navigation
      e.preventDefault();

      // Push the current state back to prevent navigation
      window.history.pushState(null, '', window.location.href);

      // Increment the counter since we added an extra history entry
      extraHistoryEntries.current += 1;

      // Show custom modal
      setIsBackButtonTriggered(true);
      setShowLeaveModal(true);
    };

    if (enabled) {
      // Push a state to the history to catch back button
      window.history.pushState(null, '', window.location.href);
      // Increment the counter for this initial pushState
      extraHistoryEntries.current += 1;
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [enabled]);

  const LeavePassportsModal: React.FC<{ isVisible: boolean; onClose: () => void; onLeave: () => void }> = ({
    isVisible,
    onClose,
    onLeave,
  }) => (
    <Modal visible={isVisible} onModalClosed={onClose} disableClickOutside title='Leave "ALL CASE PASSPORTS" PAGE'>
      <div className={modalStyles['leave-modal-body']}>
        <img src={warningIcon} className={modalStyles['icon']} />
        <div className={modalStyles['leave-modal-body-content']} data-leave-modal-content>
          <p>
            You have a placeholder passport section that is missing the required information to complete and save the
            passport. If you wish to leave the page, the incomplete passport details will not be saved.{' '}
          </p>
          <p className={modalStyles['continue-text']}>Do you wish to continue?</p>
          <Button
            className={modalStyles['leave-modal-button']}
            variant={ButtonVariant.outlined}
            styleType={ButtonStyle.secondary}
            onClick={onLeave}
          >
            Continue
          </Button>
        </div>
      </div>
    </Modal>
  );

  return (
    <Context.Provider
      value={{
        enabled,
        setEnabled,
        showLeaveModal,
        setShowLeaveModal,
        handleLeave,
        setPendingLeaveTarget,
        pendingUrl,
        setPendingUrl,
      }}
    >
      {' '}
      {children}
      <LeavePassportsModal
        isVisible={showLeaveModal}
        onClose={handleModalClose}
        onLeave={isBackButtonTriggered ? handleBackButtonLeave : handleLeave}
      />
    </Context.Provider>
  );
};

export const useInterceptor = () => {
  const ctx = useContext(Context);
  if (!ctx) throw new Error('useInterceptor must be inside InterceptorProvider');
  return ctx;
};
